import React, { useState, useEffect } from 'react';
import { Gift, Users, Share2, Check, Trophy, Ticket } from 'lucide-react';
import { useTheme } from '../App';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Widget for user profile page showing their referral progress and tickets
 */
const ReferralProgressCard = ({ className = '' }) => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        // First initialize referral code if needed
        await fetch(`${API_URL}/api/referrals/initialize-referral-code`, {
          method: 'POST',
          credentials: 'include'
        });
        
        // Then get progress
        const res = await fetch(`${API_URL}/api/referrals/my-progress`, {
          credentials: 'include'
        });
        
        if (res.ok) {
          const data = await res.json();
          setProgress(data);
        }
      } catch (error) {
        console.error('Failed to fetch referral progress:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, []);

  const handleShare = () => {
    if (!progress?.referral_code) return;
    
    const siteUrl = window.location.origin;
    const refLink = `${siteUrl}/?ref=${progress.referral_code}`;
    const message = encodeURIComponent(
      `Check deze marktplaats! Ik maak kans op 100 XCG Cash als jij ook even kijkt: ${refLink}`
    );
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  if (loading) {
    return (
      <div className={`animate-pulse rounded-xl p-4 ${isDark ? 'bg-white/5' : 'bg-gray-100'} ${className}`}>
        <div className={`h-4 w-1/3 rounded ${isDark ? 'bg-white/10' : 'bg-gray-200'} mb-3`}></div>
        <div className={`h-8 w-full rounded ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}></div>
      </div>
    );
  }

  if (!progress) return null;

  const progressPercent = Math.min((progress.total_visits / progress.target) * 100, 100);

  return (
    <div className={`rounded-xl border overflow-hidden ${
      isDark ? 'bg-gradient-to-br from-slate-800/50 to-slate-900/50 border-white/10' : 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200'
    } ${className}`}>
      {/* Header */}
      <div className={`px-4 py-3 flex items-center gap-3 border-b ${
        isDark ? 'border-white/10' : 'border-amber-200'
      }`}>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          progress.goal_reached 
            ? 'bg-green-500' 
            : 'bg-gradient-to-br from-amber-400 to-orange-500'
        }`}>
          {progress.goal_reached ? (
            <Trophy className="w-5 h-5 text-white" />
          ) : (
            <Gift className="w-5 h-5 text-white" />
          )}
        </div>
        <div>
          <h3 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Share & Win Actie
          </h3>
          <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            Win 100 XCG Cash
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Progress indicators */}
        <div className="flex items-center justify-between mb-3">
          {[1, 2, 3].map((num) => (
            <div key={num} className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                num <= progress.total_visits
                  ? progress.goal_reached 
                    ? 'bg-green-500 text-white'
                    : 'bg-amber-500 text-white'
                  : isDark 
                    ? 'bg-white/10 text-white/30'
                    : 'bg-gray-200 text-gray-400'
              }`}>
                {num <= progress.total_visits ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <Users className="w-4 h-4" />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Progress bar */}
        <div className={`h-2 rounded-full overflow-hidden mb-3 ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
          <div 
            className={`h-full rounded-full transition-all duration-500 ${
              progress.goal_reached 
                ? 'bg-gradient-to-r from-green-400 to-emerald-500' 
                : 'bg-gradient-to-r from-amber-400 to-orange-500'
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        {/* Stats row */}
        <div className="flex items-center justify-between mb-3">
          <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            {progress.total_visits}/{progress.target} bezoeken
          </span>
          {progress.total_signups > 0 && (
            <span className="text-xs text-green-500 font-medium">
              +{progress.total_signups} registratie(s)
            </span>
          )}
        </div>

        {/* Tickets display */}
        {progress.total_tickets > 0 && (
          <div className={`flex items-center gap-2 p-2 rounded-lg mb-3 ${
            isDark ? 'bg-green-500/10' : 'bg-green-50'
          }`}>
            <Ticket className="w-4 h-4 text-green-500" />
            <span className="text-green-600 text-sm font-medium">
              {progress.total_tickets} ticket{progress.total_tickets > 1 ? 's' : ''} voor de loting
            </span>
          </div>
        )}

        {/* Referral code display */}
        <div className={`flex items-center justify-between p-2 rounded-lg mb-3 ${
          isDark ? 'bg-white/5' : 'bg-white'
        }`}>
          <span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Jouw code:</span>
          <code className={`font-mono font-bold ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>
            {progress.referral_code}
          </code>
        </div>

        {/* Share button */}
        <button
          onClick={handleShare}
          className="w-full py-2.5 px-4 bg-[#25D366] text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-2 hover:bg-[#20bd5a] transition-colors"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
          </svg>
          Deel via WhatsApp
          <Share2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default ReferralProgressCard;
